import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading } from "@/components/common/ui";
import { cn } from "@/lib/utils";
import { RotateCcw, Lock } from "lucide-react";

const VERT_ACCENT = {
  "ATL/BTL": "hsl(var(--chart-cat-4))",
  CX: "hsl(var(--chart-cat-6))",
  Digital: "hsl(var(--chart-cat-3))",
};

// Column order per requirement: Leads, SV, Booking (marketing also keeps Revenue).
const BENCH_FIELDS = [
  { k: "lead", label: "Leads" },
  { k: "sv", label: "SV" },
  { k: "booking", label: "Booking" },
];
const MKT_FIELDS = [...BENCH_FIELDS, { k: "revenue", label: "Rev (Cr)" }];

const fmt = (v, isRev) => (isRev ? `₹${Number(v || 0).toFixed(1)}` : Math.round(Number(v || 0)).toLocaleString("en-IN"));

/**
 * VerticalTargetsEditor — edit Leads / SV / Booking (and Revenue for marketing)
 * per month, subdivided by the marketing verticals ATL/BTL, CX & Digital.
 * Benchmark edits are governed (reason + audit); marketing edits are free-form.
 */
export function VerticalTargetsEditor({ open, project, kind = "marketing", ratio = 15, canEdit = true, onClose, onSaved, onReset }) {
  const isBench = kind === "benchmark";
  const fields = isBench ? BENCH_FIELDS : MKT_FIELDS;
  const [data, setData] = useState(null);
  const [rows, setRows] = useState({});
  const [init, setInit] = useState({});
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !project) return;
    let on = true;
    setLoading(true); setReason(""); setData(null);
    api.projectVerticalTargets(project, kind)
      .then((d) => {
        if (!on) return;
        const map = {};
        (d.months || []).forEach((m) => {
          map[m.month] = {};
          (d.vertical_order || []).forEach((v) => { map[m.month][v] = { ...(m.verticals[v] || {}) }; });
        });
        setData(d); setRows(map); setInit(JSON.parse(JSON.stringify(map))); setLoading(false);
      })
      .catch(() => { if (on) { setLoading(false); toast.error("Could not load vertical targets"); } });
    return () => { on = false; };
  }, [open, project, kind]);

  const verts = data?.vertical_order || ["ATL/BTL", "CX", "Digital"];
  const months = useMemo(() => (data?.months || []).map((m) => m.month), [data]);

  const setCell = (month, vert, field, val) => {
    const num = val === "" ? 0 : parseFloat(val);
    const safe = isNaN(num) ? 0 : num;
    setRows((r) => {
      const cur = { ...r[month][vert], [field]: safe };
      // Site Visits drive Bookings via the SV→Booking ratio (Booking = SV ÷ ratio).
      if (field === "sv" && ratio > 0) cur.booking = Math.round(safe / ratio);
      return { ...r, [month]: { ...r[month], [vert]: cur } };
    });
  };
  const total = (month, field) => verts.reduce((s, v) => s + (Number(rows[month]?.[v]?.[field]) || 0), 0);

  const changedCount = useMemo(() => {
    let n = 0;
    months.forEach((month) => verts.forEach((v) => fields.forEach(({ k }) => {
      if ((rows[month]?.[v]?.[k] ?? 0) !== (init[month]?.[v]?.[k] ?? 0)) n += 1;
    })));
    return n;
  }, [rows, init, months, verts, fields]);

  const save = async () => {
    if (isBench && !reason.trim()) { toast.error("A reason is required for benchmark changes."); return; }
    setSaving(true);
    try {
      const cells = [];
      months.forEach((month) => verts.forEach((v) => fields.forEach(({ k }) => {
        const cur = rows[month]?.[v]?.[k] ?? 0;
        const was = init[month]?.[v]?.[k] ?? 0;
        if (cur !== was) cells.push({ month, vertical: v, field: k, value: cur });
      })));
      if (cells.length === 0) { toast.info("No changes to save"); setSaving(false); return; }
      await api.saveVerticalTargets({ project, kind, reason: reason.trim(), cells });
      toast.success(`Updated ${isBench ? "benchmark" : "marketing"} target · ${project} (${cells.length} value${cells.length === 1 ? "" : "s"})`);
      onSaved?.(); onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent className="flex w-full flex-col overflow-hidden p-0 sm:max-w-3xl" data-testid={`${kind}-vertical-editor`}>
        <SheetHeader className="border-b p-5">
          <SheetTitle className="flex items-center gap-2">
            {isBench && <Lock className="h-4 w-4 text-muted-foreground" />}
            Edit {isBench ? "Management Benchmark" : "Marketing Target"} · {project}
          </SheetTitle>
          <SheetDescription>
            {isBench
              ? `Governed edit of the board benchmark. Each month is split by vertical (ATL/BTL, CX, Digital). Bookings auto-fill from Site Visits at 1:${ratio}. A reason is required and every change is audited.`
              : `Set Leads, Site Visits & Bookings per month, split by vertical (ATL/BTL, CX, Digital). Bookings auto-fill from Site Visits at 1:${ratio} (you can still override). Channel Partners are managed separately.`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-4">
          {loading || !data ? <Loading rows={8} /> : (
            <table className="w-full border-collapse text-sm" data-testid={`${kind}-vertical-editor-table`}>
              <thead className="sticky top-0 z-10 bg-card">
                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-2 py-2 text-left font-medium">Vertical</th>
                  {fields.map((f) => <th key={f.k} className="px-2 py-2 text-right font-medium">{f.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {months.map((month) => (
                  <React.Fragment key={month}>
                    <tr className="bg-[hsl(var(--surface-2))]" data-testid={`vertical-editor-month-${month}`}>
                      <td className="px-2 py-1.5 text-xs font-semibold">{month}</td>
                      {fields.map((f) => (
                        <td key={f.k} className="px-2 py-1.5 text-right text-[11px] font-semibold tabular-nums text-muted-foreground">
                          {fmt(total(month, f.k), f.k === "revenue")}
                        </td>
                      ))}
                    </tr>
                    {verts.map((v) => (
                      <tr key={`${month}-${v}`} className="border-b last:border-0" data-testid={`vertical-editor-row-${month}-${v.replace(/[^a-zA-Z]+/g, "-")}`}>
                        <td className="px-2 py-1.5 pl-4">
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className="h-2 w-2 rounded-full" style={{ background: VERT_ACCENT[v] || "hsl(var(--primary))" }} />
                            {v}
                          </span>
                        </td>
                        {fields.map((f) => (
                          <td key={f.k} className="px-1 py-1">
                            <Input
                              type="number"
                              value={rows[month]?.[v]?.[f.k] ?? ""}
                              disabled={!canEdit}
                              onChange={(e) => setCell(month, v, f.k, e.target.value)}
                              className="ml-auto h-8 w-[84px] text-right text-sm tabular-nums"
                              data-testid={`vertical-input-${month}-${v.replace(/[^a-zA-Z]+/g, "-")}-${f.k}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {isBench && !loading && data && (
            <div className="mt-4">
              <label className="mb-1 block text-xs font-medium">Reason for change (required)</label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Board revised Q1 site-visit benchmark" data-testid="vertical-editor-reason" />
            </div>
          )}
        </div>

        <SheetFooter className="flex-row items-center justify-between border-t p-4">
          {!isBench && onReset ? (
            <Button variant="outline" size="sm" onClick={() => onReset(project)} disabled={!canEdit || saving} data-testid="vertical-editor-reset">
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset to benchmark
            </Button>
          ) : <span className="text-[11px] text-muted-foreground">{changedCount > 0 ? `${changedCount} value${changedCount === 1 ? "" : "s"} changed` : "No changes yet"}</span>}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onClose?.()}>Cancel</Button>
            <Button onClick={save} disabled={!canEdit || saving || (isBench && !reason.trim())} data-testid={`${kind}-vertical-editor-save`}>
              {saving ? "Saving…" : isBench ? "Save & log" : "Save changes"}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
